import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Paintbrush, ScrollText, Music, Users, Loader as Loader2 } from 'lucide-react';
import { SingAlongService } from '../services/SingAlongService';
import { ContestService } from '../services/ContestService';
import { KioskService } from '../services/KioskService';
import { BrandingTab, RulesTab, SongsTab, ParticipantsTab, TabBtn } from '../components/kiosk/LeagueEditorTabs';

type Tab = 'branding' | 'rules' | 'songs' | 'participants';

export const ContestManage: React.FC = () => {
  const navigate = useNavigate();
  const { leagueId: paramLeagueId } = useParams<{ leagueId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) || 'branding';
  const setTab = (t: Tab) => setSearchParams({ tab: t }, { replace: true });

  const [leagueId, setLeagueId] = useState<string | null>(null);
  const [league, setLeague] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubscription, setIsSubscription] = useState(false);

  useEffect(() => {
    (async () => {
      const user = await SingAlongService.getCurrentUser();
      if (!user || !user.email_confirmed_at) {
        navigate('/sing-along/signup', { replace: true });
        return;
      }
      try {
        const hasSub = await ContestService.hasActiveSubscription();
        setIsSubscription(hasSub);

        let targetLeagueId = paramLeagueId || null;

        if (!targetLeagueId) {
          navigate('/contests/dashboard', { replace: true });
          return;
        }

        setLeagueId(targetLeagueId);
        const data = await KioskService.adminGetLeague(targetLeagueId);
        if (!data) { setError('Contest not found.'); return; }
        setLeague(data as Record<string, unknown>);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load contest');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, paramLeagueId]);

  const update = async (patch: Record<string, unknown>) => {
    if (!leagueId) return;
    try {
      setSaving(true);
      setError(null);
      const data = await KioskService.adminUpdateLeague(leagueId, patch);
      setLeague(data as Record<string, unknown>);
      setSuccess('Saved');
      setTimeout(() => setSuccess(null), 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
      </div>
    );
  }

  if (!league || !leagueId) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-slate-400 mb-4">{error || 'Contest not found.'}</p>
          <Link to="/contests/plans" className="text-sky-400 hover:text-sky-300">Back to plans</Link>
        </div>
      </div>
    );
  }

  const l = league as Record<string, string | number | null | string[] | boolean>;
  const backLink = '/contests/dashboard';

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="absolute inset-0 bg-gradient-to-br from-sky-600/10 via-transparent to-emerald-600/10 pointer-events-none" />
      <div className="relative p-6 max-w-6xl mx-auto">
        <Link to={backLink} className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-4">
          <ArrowLeft className="w-4 h-4" /> {isSubscription ? 'My Contests' : 'Contests'}
        </Link>

        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">{l.name as string}</h1>
            <div className="text-sm text-slate-400 font-mono">/league/{l.slug as string}</div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`/league/${l.slug}`}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
            >
              Preview
            </a>
            <a
              href={`/league/${l.slug}/leaderboard`}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
            >
              Leaderboard
            </a>
          </div>
        </div>

        {error && <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">{error}</div>}
        {success && <div className="mb-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-300 text-sm">{success}</div>}

        <div className="border-b border-slate-700 flex gap-1 mb-6 flex-wrap">
          <TabBtn active={tab === 'branding'} onClick={() => setTab('branding')} icon={<Paintbrush className="w-4 h-4" />} label="Branding & Sponsors" />
          <TabBtn active={tab === 'rules'} onClick={() => setTab('rules')} icon={<ScrollText className="w-4 h-4" />} label="Rules" />
          <TabBtn active={tab === 'songs'} onClick={() => setTab('songs')} icon={<Music className="w-4 h-4" />} label="Songs" />
          <TabBtn active={tab === 'participants'} onClick={() => setTab('participants')} icon={<Users className="w-4 h-4" />} label="Participants" />
        </div>

        {tab === 'branding' && <BrandingTab league={l} onSave={update} saving={saving} />}
        {tab === 'rules' && <RulesTab league={l} onSave={update} saving={saving} />}
        {tab === 'songs' && <SongsTab leagueId={leagueId} />}
        {tab === 'participants' && <ParticipantsTab leagueId={leagueId} />}
      </div>
    </div>
  );
};

export default ContestManage;
